import { useEffect, useState, useRef } from "react";
import { PlaneIconHero, GamepadIcon, BypassIcon, GlobeSwitchIcon } from "./Icons";
import ScrollIndicator from "./ScrollIndicator";

function Hero() {
  const [isReady, setIsReady] = useState(false);
  const [clusterOffset, setClusterOffset] = useState(0);
  const heroRef = useRef(null);
  const clusterOffsetRef = useRef(0);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsReady(true);
    });
  }, []);

  useEffect(() => {
    const heroEl = heroRef.current;
    const contentEl = heroEl?.querySelector(".hero-content");
    const bgEl = heroEl?.querySelector(".hero-bg-icon");
    const indicatorEl = heroEl?.querySelector(".hero-scroll-indicator");

    if (!heroEl || !contentEl || !bgEl) {
      return undefined;
    }

    let frameId = null;
    let settleFrameId = null;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const applyClusterOffset = (nextOffset) => {
      if (Math.abs(clusterOffsetRef.current - nextOffset) < 1) {
        return false;
      }

      clusterOffsetRef.current = nextOffset;
      setClusterOffset(nextOffset);
      return true;
    };

    const queueSettledLayoutUpdate = () => {
      if (settleFrameId !== null) {
        window.cancelAnimationFrame(settleFrameId);
      }

      settleFrameId = window.requestAnimationFrame(() => {
        settleFrameId = window.requestAnimationFrame(() => {
          settleFrameId = null;
          requestLayoutUpdate();
        });
      });
    };

    const updateLayout = () => {
      frameId = null;

      const heroRect = heroEl.getBoundingClientRect();
      const contentRect = contentEl.getBoundingClientRect();
      const bgRect = bgEl.getBoundingClientRect();
      const indicatorRect = indicatorEl?.getBoundingClientRect();
      const heroStyles = window.getComputedStyle(heroEl);
      const paddingTop = parseFloat(heroStyles.paddingTop) || 0;
      const paddingBottom = parseFloat(heroStyles.paddingBottom) || 0;
      const innerTop = heroRect.top + paddingTop;
      const innerBottom = heroRect.bottom - paddingBottom;
      const aspectRatio = window.innerWidth / Math.max(window.innerHeight || 1, 1);
      const isLandscape = window.matchMedia("(orientation: landscape)").matches;

      if (!isLandscape && aspectRatio < 1.3) {
        const didChange = applyClusterOffset(0);

        if (didChange) {
          queueSettledLayoutUpdate();
        }

        return;
      }

      const clusterTop = Math.min(bgRect.top, contentRect.top);
      const clusterBottom = Math.max(indicatorRect?.bottom ?? contentRect.bottom, contentRect.bottom);
      const currentCenter = (clusterTop + clusterBottom) / 2;
      const targetCenter = innerTop + (innerBottom - innerTop) / 2;
      const maxUp = clusterTop - innerTop;
      const maxDown = innerBottom - clusterBottom;
      const nextOffset = Math.round(
        clamp(targetCenter - currentCenter, -maxUp, maxDown)
      );

      const didChange = applyClusterOffset(nextOffset);

      if (didChange) {
        queueSettledLayoutUpdate();
      }
    };

    const requestLayoutUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateLayout);
    };

    const requestViewportLayoutUpdate = () => {
      requestLayoutUpdate();
      queueSettledLayoutUpdate();
    };

    const resizeObserver = new ResizeObserver(requestViewportLayoutUpdate);
    resizeObserver.observe(heroEl);
    resizeObserver.observe(contentEl);
    resizeObserver.observe(bgEl);

    if (indicatorEl) {
      resizeObserver.observe(indicatorEl);
    }

    requestViewportLayoutUpdate();

    window.addEventListener("resize", requestViewportLayoutUpdate, { passive: true });
    window.visualViewport?.addEventListener("resize", requestViewportLayoutUpdate, { passive: true });
    window.addEventListener("orientationchange", requestViewportLayoutUpdate, { passive: true });

    return () => {
      window.removeEventListener("resize", requestViewportLayoutUpdate);
      window.visualViewport?.removeEventListener("resize", requestViewportLayoutUpdate);
      window.removeEventListener("orientationchange", requestViewportLayoutUpdate);
      resizeObserver.disconnect();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      if (settleFrameId !== null) {
        window.cancelAnimationFrame(settleFrameId);
      }
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="hero"
      style={{ "--hero-cluster-offset": `${clusterOffset}px` }}
    >
      {/* Иконка */}
      <div className={`hero-bg-icon${isReady ? ' hero-bg-icon--visible' : ''}`}>
        <PlaneIconHero />
      </div>

      {/* Контент */}
      <div className="hero-content">
        <h1>Добро пожаловать!</h1>
        <p>
          Защитите своё соединение и используйте подключение для любых задач
        </p>

        {/* Ключевые преимущества */}
        <div className="hero-stats">
          <div className="hero-stats-item">
            <GamepadIcon className="hero-stats-icon" />
            <span>Игр</span>
          </div>
          <div className="hero-stats-item">
            <BypassIcon className="hero-stats-icon" />
            <span>Обход ограничений</span>
          </div>
          <div className="hero-stats-item">
            <GlobeSwitchIcon className="hero-stats-icon" />
            <span>Смена региона</span>
          </div>
        </div>
      </div>

      <ScrollIndicator threshold={50} />
    </section>
  );
}

export default Hero;
